"use client";

import React, { useMemo } from "react";
import { ORDER_STATUS_LABELS } from "../services/order-service";
import { parseServerDate, formatServerDateTime } from "../utils/datetime";
import ImageCustom from "../../../components/custom/image-custom";

// Lấy danh sách ảnh của 1 log (cột images là json, có thể là mảng hoặc chuỗi JSON).
function getStepImages(raw: any): string[] {
  const v = raw?.images;
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "string") {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p.filter(Boolean) : v ? [v] : [];
    } catch {
      return v ? [v] : [];
    }
  }
  return [];
}

// classification_data lưu ở workflow_logs có thể là object hoặc chuỗi JSON.
function parseCD(raw: any): any {
  if (!raw) return null;
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw;
}

// 4 chế độ kiểm đếm/phân loại — mỗi mode = 1 cột trong ma trận đối soát.
type ClassifyMode = "counting" | "pickup_check" | "inspect" | "transfer_check" | "production_done" | "delivery_pack" | "delivery_check";
const MODE_ORDER: ClassifyMode[] = ["counting", "pickup_check", "inspect", "transfer_check", "production_done", "delivery_pack", "delivery_check"];
// Các mode ghi nhận theo TỪNG TÚI (có breakdown bags[])
const PER_BAG_MODES: ClassifyMode[] = ["pickup_check", "delivery_pack"];
const MODE_LABEL: Record<ClassifyMode, string> = {
  counting: "Kiểm đếm (nhận)",
  pickup_check: "Tài xế lấy",
  inspect: "Phân loại (CH)",
  transfer_check: "Đối chiếu (nơi nhận)",
  production_done: "Hoàn thành giặt",
  delivery_pack: "Đóng túi giao",
  delivery_check: "Kiểm đếm giao",
};

// Tên bước (công đoạn) suy luận từ transition từ_status → to_status.
// Mỗi chặng = quãng giữa 2 mốc; tên đặt theo HÀNH ĐỘNG hoàn tất chặng (mốc đến).
function stepLabel(from: number, to: number): string {
  if (!from || from === 0) return "Tạo đơn";
  switch (to) {
    case 9: return "NV kiểm kê xác nhận đơn";
    case 19: return "Kiểm đếm với khách";
    case 20: return "Giao cho tài xế (broadcast)";
    case 2: return from === 20 ? "Tài xế nhận & bàn giao" : "Tài xế xác nhận đơn";
    case 10: return from === 2 ? "Lấy hàng về cửa hàng & bàn giao" : "Kiểm tra / phân loại";
    case 3: return "Kiểm tra/phân loại → chờ giặt";
    case 11: case 4: return "Giặt";
    case 15: return "Giặt xong → chờ sấy";
    case 12: return "Sấy";
    case 16: return "Sấy xong → chờ ủi";
    case 13: return "Ủi";
    case 17: return "Ủi xong → chờ gấp";
    case 14: return "Gấp";
    case 6: return "Giặt hoàn tất - sẵn sàng giao";
    case 7: return "Tài xế nhận đơn giao";
    case 18: return "Kiểm đếm khi giao (với khách)";
    // Hai log cùng về COMPLETE (kiểm đếm giao rồi chốt đơn) — không tách nhãn thì bảng ra 2 dòng trùng tên.
    case 5: return from === 18 ? "Chốt hoàn tất đơn" : "Giao xong & hoàn tất";
    case 8: return "Huỷ đơn";
    default: return ORDER_STATUS_LABELS[to] || `→ Trạng thái ${to}`;
  }
}

// ===== Bảng 2: chặng thời gian =====
// Mỗi chặng = quãng giữa 2 mốc, phân theo 3 pha của quy trình và 3 loại thời gian.
type SegKind = "wait" | "work" | "transport";
type Phase = "receive" | "production" | "deliver";
const PHASE_LABEL: Record<Phase, string> = { receive: "NHẬN", production: "SẢN XUẤT", deliver: "GIAO" };
const KIND_LABEL: Record<SegKind, string> = { wait: "Chờ", work: "Thực hiện", transport: "Vận chuyển" };
const KIND_CLS: Record<SegKind, string> = {
  wait: "bg-amber-100 text-amber-700",
  work: "bg-slate-100 text-slate-600",
  transport: "bg-indigo-100 text-indigo-700",
};

const RECEIVE_STATUSES = [1, 2, 9, 10, 19, 20];
const PRODUCTION_STATUSES = [3, 4, 6, 11, 12, 13, 14, 15, 16, 17];
const DELIVER_STATUSES = [5, 7, 8, 18];

function phaseOfStatus(to: number): Phase {
  if (RECEIVE_STATUSES.includes(to)) return "receive";
  if (DELIVER_STATUSES.includes(to)) return "deliver";
  if (PRODUCTION_STATUSES.includes(to)) return "production";
  return "production";
}

/**
 * Nhãn chặng — KHÁC nhãn log: gộp cặp "làm xong X → chờ Y" về đúng một tên công đoạn, để hai log liên tiếp
 * của cùng một bước (vd 11 "Giặt" rồi 15 "Giặt xong → chờ sấy") gộp lại thành một dòng "Giặt".
 */
function segmentOf(from: number, to: number): { label: string; kind: SegKind } {
  switch (to) {
    case 20: return { label: "Chờ điều phối tài xế", kind: "wait" };
    case 2: return { label: "Chờ tài xế xác nhận", kind: "wait" };
    case 9: return { label: "Kiểm kê xác nhận đơn", kind: "work" };
    case 19: return { label: "Kiểm đếm với khách", kind: "work" };
    case 10:
      return from === 2
        ? { label: "Tài xế lấy hàng & mang về", kind: "transport" }
        : { label: "Kiểm tra / phân loại", kind: "work" };
    case 3: return { label: "Kiểm tra / phân loại", kind: "work" };
    case 4: case 11: case 15: return { label: "Giặt", kind: "work" };
    case 12: case 16: return { label: "Sấy", kind: "work" };
    case 13: case 17: return { label: "Ủi", kind: "work" };
    case 14: return { label: "Gấp", kind: "work" };
    case 6: return { label: "Hoàn tất sản xuất", kind: "work" };
    case 7: return { label: "Chờ tài xế nhận giao", kind: "wait" };
    case 18: return { label: "Kiểm đếm khi giao", kind: "work" };
    case 5: return { label: "Giao khách", kind: "work" };
    case 8: return { label: "Huỷ đơn", kind: "work" };
    default: return { label: ORDER_STATUS_LABELS[to] || `Bước ${to}`, kind: "work" };
  }
}

const FAULT_TYPE: Record<number, string> = {
  1: "Giao trễ", 2: "Thiếu đồ", 3: "Hư hỏng", 4: "Không nộp quỹ",
  5: "Sai quy trình", 6: "Quá hạn xác nhận", 7: "Không điều phối đơn", 99: "Khác",
};
/** Phiếu đã "Hủy lỗi" — dùng để gạch ngang lý do ở ma trận đối soát. */
const FAULT_STATUS_REJECTED = 4;
const FAULT_STATUS: Record<number, { text: string; cls: string }> = {
  1: { text: "Chờ giải trình", cls: "bg-amber-100 text-amber-700" },
  2: { text: "Đã giải trình", cls: "bg-blue-100 text-blue-700" },
  3: { text: "Đã chốt", cls: "bg-green-100 text-green-700" },
  4: { text: "Đã hủy", cls: "bg-slate-100 text-slate-600" },
};

// Coi datetime từ BE là UTC (xem utils/datetime) → giờ tuyệt đối hiển thị đúng giờ địa phương.
function parseDate(v: any): Date | null {
  return parseServerDate(v);
}
// Tách ngày/giờ thành 2 dòng: "DD/MM/YYYY" trên, "HH:mm" dưới.
function fmtDateLines(v: any): { date: string; time: string } | null {
  const d = parseDate(v);
  if (!d) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}
function fmtMins(mins: number | null): string {
  if (mins == null) return "—";
  if (mins < 1) return "< 1 phút";
  if (mins < 60) return `${mins} phút`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}g ${m}p` : `${h} giờ`;
}
function formatMoney(v: any): string {
  return Number(v || 0).toLocaleString("vi-VN");
}

// Ô thời gian 2 dòng: ngày trên, giờ:phút dưới.
const TimeCell: React.FC<{ v: Date | null }> = ({ v }) => {
  const dl = v ? fmtDateLines(v) : null;
  if (!dl) return <span className="text-slate-300">—</span>;
  return (
    <div className="leading-tight">
      <div className="text-slate-600">{dl.date}</div>
      <div className="text-slate-500">{dl.time}</div>
    </div>
  );
};

// Loại bút toán sổ nợ đồ (BE `ITEM_DEBT_ENTRY_TYPE`) — cột "Nợ / trả". Thứ tự = thứ tự hiện nhãn trong ô.
const DEBT_ENTRY_META: { type: number; label: string; cls: string }[] = [
  { type: 1, label: "Nợ", cls: "bg-blue-100 text-blue-700" },
  { type: 2, label: "Trả nợ", cls: "bg-emerald-100 text-emerald-700" },
  { type: 4, label: "Thất thoát", cls: "bg-red-100 text-red-700" },
  { type: 5, label: "Không chuẩn", cls: "bg-amber-100 text-amber-800" },
  { type: 6, label: "Giao dư", cls: "bg-violet-100 text-violet-700" },
  { type: 3, label: "Điều chỉnh", cls: "bg-slate-200 text-slate-700" },
];

/**
 * Ô "Nợ / trả" — hiện như phép cộng/trừ ĐỐI VỚI KHÁCH giữa đóng túi và số khách nhận: sổ ghi dương = khách nhận
 * thiếu (nợ / thất thoát / không chuẩn / điều chỉnh tăng nợ) → "−n"; sổ ghi âm = khách nhận thêm (trả nợ / giao dư) → "+n".
 */
const DebtChips: React.FC<{ byType?: Map<number, number> }> = ({ byType }) => {
  const chips = DEBT_ENTRY_META.filter((m) => (byType?.get(m.type) || 0) !== 0);
  if (chips.length === 0) return <span className="text-slate-300">—</span>;
  return (
    <div className="flex flex-col items-center gap-1">
      {chips.map((m) => {
        const delta = -byType!.get(m.type)!;
        return (
          <span key={m.type} className={`px-1.5 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${m.cls}`}>
            {delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`} ({m.label.toLowerCase()})
          </span>
        );
      })}
    </div>
  );
};

interface ClassifyGroup {
  mode: ClassifyMode;
  items: { category: string; quantity: number; category_id?: number | null }[];
  total: number;
  missingNote?: string;
  confirmedMissing?: boolean;
}

const EQUIPMENT_TYPE_NAME: Record<number, string> = {
  1: "Túi", 2: "Máy giặt", 3: "Máy sấy", 4: "Bàn ủi", 5: "Bàn gấp", 6: "Sọt",
};

interface Props {
  order: any;
  workflowLogs?: any[];
  transfers?: any[];
  classifyGroups: ClassifyGroup[];
  faults: any[];
  faultsLoading?: boolean;
  equipmentDetail?: any[];
}

const OrderProcessTab: React.FC<Props> = ({ order, workflowLogs, transfers, classifyGroups, faults, faultsLoading, equipmentDetail }) => {
  const employees: any[] = useMemo(() => order?.employees ?? [], [order]);

  // Map employee_id -> tên (để hiển thị "người thực hiện" trong timeline)
  const empNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of employees) {
      const name = e.employee?.name;
      if (name) map.set(Number(e.employee_id), name);
    }
    return map;
  }, [employees]);
  const nameOf = (uid: any): string => {
    const id = Number(uid);
    if (!id) return "Hệ thống";
    return empNameMap.get(id) || `NV #${id}`;
  };
  // Thay "#123" trong note (vd ghi chú bàn giao "tài xế #5 → NV #7") bằng tên NV nếu biết.
  const prettyNote = (note: string): string =>
    note.replace(/#(\d+)/g, (_m, id) => empNameMap.get(Number(id)) || `#${id}`);

  // Timeline từ workflow_logs: mỗi dòng = 1 chặng (bỏ log lưu phân loại from==to).
  // Bắt đầu = mốc trước, Kết thúc = mốc này, Thời gian thực hiện = hiệu 2 mốc.
  const timeline = useMemo(() => {
    const logs = (workflowLogs || [])
      .map((l) => ({ raw: l, t: parseDate(l.performed_at) || parseDate(l.created_at), cd: parseCD(l.classification_data) }))
      // Chỉ bỏ log LƯU SỐ LƯỢNG (`classification_data.mode`) — đó là dữ liệu kiểm đếm, không phải mốc tiến trình.
      // Trước đây lọc theo `from_status !== to_status`, mà bàn giao xe trung chuyển chỉ đổi NGƯỜI GIỮ chứ không
      // đổi bước → cả chặng xe biến mất khỏi bảng. Cron gỡ tài xế cũng cùng cảnh.
      // Bỏ cả log ghi nhận hàng không chuẩn (`type: 'defect_report'`) và báo vấn đề (from_step_code 'ISSUE'/'DEFECT')
      // — không phải mốc tiến trình.
      // 'final_weight_update' / 'item_edit': log điều chỉnh sản lượng, SL, đơn giá — hiện riêng ở
      // khối "Điều chỉnh số lượng / đơn giá" bên dưới, không phải mốc tiến trình.
      .filter((x) => x.t && !x.cd?.mode
        && x.cd?.type !== 'defect_report' && x.cd?.type !== 'final_weight_update' && x.cd?.type !== 'item_edit'
        && x.raw?.from_step_code !== 'ISSUE' && x.raw?.from_step_code !== 'DEFECT'
        && x.raw?.from_step_code !== 'FINAL_WEIGHT' && x.raw?.from_step_code !== 'ITEM_EDIT')
      .sort((a, b) => a.t!.getTime() - b.t!.getTime());
    return logs.map((x, i) => {
      const fromTime = i > 0 ? logs[i - 1].t! : null;
      const toTime = x.t!;
      const durMins = fromTime ? Math.max(0, Math.round((toTime.getTime() - fromTime.getTime()) / 60000)) : null;
      const from = Number(x.raw.from_status);
      const to = Number(x.raw.to_status);
      const isCreate = !from || from === 0;
      const isHandover = Number(x.raw.qr_scanned) === 1 || /bàn giao/i.test(String(x.raw.note || ""));
      // Người thực hiện công đoạn: mặc định = người ghi log (employee_id).
      // Riêng bước "Lấy hàng về cửa hàng" (from=WAITING_PICK_UP, có bàn giao): log.employee_id là
      // người NHẬN (NV cửa hàng) — nhưng công đoạn này là TÀI XẾ mang hàng về, nên lấy người GIAO
      // (#id đầu tiên trong note "tài xế #G → NV #R").
      let performerId = Number(x.raw.employee_id);
      if (isHandover && from === 2) {
        const m = String(x.raw.note || "").match(/#(\d+)/);
        if (m) performerId = Number(m[1]);
      }
      // Điều chuyển / Tách đơn: label rõ nghĩa (BE gắn transfer_action / split_action). performer_name do BE resolve.
      const transferAction = x.raw.transfer_action;
      // Bàn giao xe trung chuyển: from_status = to_status (chỉ đổi NGƯỜI giữ đồ, không đổi bước) nên
      // `stepLabel` không nói lên gì — lấy thẳng nhãn từ note do BE dựng ("Bàn giao lên xe … (51C-…)").
      const label =
        x.raw.split_action ? "Tách đơn con"
          : x.raw.vehicle_action ? String(x.raw.note || "Bàn giao xe trung chuyển").split(":")[0]
            : transferAction === "TRANSFER_CREATE" ? "Tạo phiếu điều chuyển"
              : transferAction === "TRANSFER_RECEIVE" ? "Xác nhận nhận điều chuyển"
                // from == to mà không phải hành động nào ở trên: log ghi chú (vd cron gỡ tài xế) — `stepLabel`
                // chỉ trả về tên trạng thái, đọc như thể đơn vừa đổi bước.
                : from === to ? "Cập nhật (không đổi bước)"
                  : stepLabel(from, to);
      return {
        raw: x.raw, cd: x.cd, fromTime, toTime, durMins, label, isHandover, isCreate, performerId,
        performerName: x.raw.performer_name as string | undefined,
        performedBranch: x.raw.branch_name as string | undefined,
        plate: (x.raw.vehicle_action ? x.cd?.plate_number : null) as string | null,
        transferAction: transferAction as string | null,
        isVehicle: !!x.raw.vehicle_action,
        from, to,
      };
    });
  }, [workflowLogs]);

  // Bỏ dòng "Tạo đơn" khỏi bảng (chỉ dùng làm mốc Bắt đầu cho bước kế tiếp).
  const displayedSteps = useMemo(() => timeline.filter((s) => !s.isCreate), [timeline]);

  // Tên chi nhánh theo id — dựng từ phiếu điều chuyển (đủ cho nhãn "Vận chuyển: A → B").
  const branchNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const t of transfers || []) {
      if (t.from_branch_id) m.set(Number(t.from_branch_id), t.from_branch_name || `CN #${t.from_branch_id}`);
      if (t.to_branch_id) m.set(Number(t.to_branch_id), t.to_branch_name || `CN #${t.to_branch_id}`);
    }
    if (order?.branch?.id) m.set(Number(order.branch.id), order.branch.name);
    return m;
  }, [transfers, order]);

  // ===== Bảng 2: thời gian từng chặng =====
  // Dựng từ chính các mốc log (không phải từ phiếu điều chuyển như trước) nên chạy đúng cho MỌI đơn:
  // đơn thường, đơn điều chuyển nhiều chặng, đơn đi xe trung chuyển hai chiều, đơn giặt lại.
  // Gộp các mốc liền nhau cùng công đoạn (vd "Giặt" rồi "Giặt xong → chờ sấy") thành một dòng.
  const journeySegments = useMemo(() => {
    type Seg = {
      phase: Phase; kind: SegKind; label: string;
      from: Date | null; to: Date | null; mins: number;
      badges: string[];
    };
    const segs: Seg[] = [];
    let seenProduction = false; // đã bắt đầu sản xuất chưa → xếp chặng vận chuyển vào NHẬN hay GIAO
    let seenReady = false;      // đã "sẵn sàng giao" chưa
    // Mốc mở đầu chặng đang dựng. Log ghi chú (không đổi bước) KHÔNG chốt mốc — nếu chốt thì quãng trước nó
    // rơi vào một dòng bị bỏ qua và biến mất khỏi tổng thời gian.
    let start: Date | null = displayedSteps[0]?.fromTime ?? null;

    for (const s of displayedSteps) {
      if (!s.toTime) continue;
      const segFrom = start ?? s.fromTime;
      if (!segFrom) continue;
      const mins = Math.max(0, Math.round((s.toTime.getTime() - segFrom.getTime()) / 60000));
      let phase: Phase;
      let kind: SegKind;
      let label: string;
      const badges: string[] = [];

      if (s.isVehicle) {
        kind = "transport";
        label = "Vận chuyển bằng xe trung chuyển";
        if (s.plate) badges.push(`Xe ${s.plate}`);
        phase = seenReady ? "deliver" : seenProduction ? "production" : "receive";
      } else if (s.transferAction === "TRANSFER_RECEIVE") {
        kind = "transport";
        const fromB = branchNameById.get(Number(s.cd?.from_branch_id));
        const toB = branchNameById.get(Number(s.cd?.to_branch_id));
        label = fromB && toB ? `Vận chuyển: ${fromB} → ${toB}` : "Vận chuyển giữa chi nhánh";
        phase = seenReady ? "deliver" : seenProduction ? "production" : "receive";
      } else if (s.transferAction === "TRANSFER_CREATE") {
        // Tạo phiếu = mốc, không phải chặng: quãng trước nó là thời gian chờ điều chuyển tại nơi gửi.
        kind = "wait";
        label = "Chờ điều chuyển";
        phase = seenReady ? "deliver" : seenProduction ? "production" : "receive";
      } else if (s.from === s.to) {
        // Log ghi chú (cron gỡ tài xế, tách đơn…): có mặt ở bảng log, nhưng không phải một chặng —
        // không chốt mốc để thời gian của nó dồn vào chặng kế tiếp.
        continue;
      } else {
        const seg = segmentOf(s.from, s.to);
        label = seg.label;
        kind = seg.kind;
        phase = phaseOfStatus(s.to);
        if (PRODUCTION_STATUSES.includes(s.to)) seenProduction = true;
        if (s.to === 6) seenReady = true;
      }
      if (s.performerName) badges.push(s.performerName);

      const last = segs[segs.length - 1];
      if (last && last.phase === phase && last.kind === kind && last.label === label) {
        last.to = s.toTime;
        last.mins += mins;
        for (const b of badges) if (!last.badges.includes(b)) last.badges.push(b);
      } else {
        segs.push({ phase, kind, label, from: segFrom, to: s.toTime, mins, badges });
      }
      start = s.toTime;
    }
    return segs;
  }, [displayedSteps, branchNameById]);

  // Chia thành các KHỐI theo pha, giữ đúng thứ tự thời gian (đơn giặt lại sẽ có 2 khối SẢN XUẤT).
  const journeyBlocks = useMemo(() => {
    const blocks: { phase: Phase; segs: typeof journeySegments }[] = [];
    for (const s of journeySegments) {
      const last = blocks[blocks.length - 1];
      if (last && last.phase === s.phase) last.segs.push(s);
      else blocks.push({ phase: s.phase, segs: [s] });
    }
    return blocks;
  }, [journeySegments]);

  const journeyTotals = useMemo(() => {
    const t = { wait: 0, work: 0, transport: 0, all: 0 };
    for (const s of journeySegments) { t[s.kind] += s.mins; t.all += s.mins; }
    return t;
  }, [journeySegments]);

  // Số kg nơi cân (nếu có nhập khi tạo phiếu — thường lượt trả về): nơi cân = chi nhánh TẠO phiếu (from).
  const transferWeights = useMemo(() => {
    return (transfers || [])
      .filter((t) => Number(t.status) !== 4 && t.weight != null && Number(t.weight) > 0)
      .map((t) => ({
        branch: t.from_branch_name || `CN #${t.from_branch_id}`,
        kg: Number(t.weight),
      }));
  }, [transfers]);

  // Fallback ảnh cho đơn CŨ: ảnh từng bước lưu ở `order.caption_images` (key = mã status),
  // KHÔNG có trong `workflow_logs.images`. Gán mỗi ảnh vào 1 bước theo trạng thái nơi chụp:
  // ưu tiên khớp from_status (vd lấy hàng=2, kiểm đếm nhận=9), fallback to_status (vd kiểm đếm giao=18).
  const captionImagesByStepIdx = useMemo(() => {
    const map = new Map<number, string[]>();
    let captionsRaw: Record<string, string | string[]> = {};
    const raw = (order as any)?.caption_images;
    if (raw) {
      try {
        captionsRaw = typeof raw === "string" ? JSON.parse(raw) : raw;
      } catch {
        captionsRaw = {};
      }
    }
    // `caption_images[status]` giờ có thể là 1 chuỗi HOẶC mảng (bước kiểm đếm nhiều ảnh) → chuẩn hoá mảng.
    const captions: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(captionsRaw || {})) {
      const urls = Array.isArray(v) ? v.filter(Boolean) : v ? [v] : [];
      if (urls.length) captions[k] = urls;
    }
    const keys = Object.keys(captions);
    if (keys.length === 0) return map;
    const consumed = new Set<string>();
    const assign = (keyOf: (s: any) => string) => {
      displayedSteps.forEach((s, idx) => {
        const k = keyOf(s);
        if (captions[k] && !consumed.has(k)) {
          const arr = map.get(idx) || [];
          arr.push(...captions[k]);
          map.set(idx, arr);
          consumed.add(k);
        }
      });
    };
    assign((s) => String(Number(s.raw.from_status)));
    assign((s) => String(Number(s.raw.to_status)));
    return map;
  }, [order, displayedSteps]);

  // Ma trận kiểm đếm: cột = các mode CÓ MẶT; hàng = hợp các loại đồ
  const { presentModes, categories, qtyOf, totalOf } = useMemo(() => {
    const groupByMode = new Map(classifyGroups.map((g) => [g.mode, g]));
    const present = MODE_ORDER.filter((m) => groupByMode.has(m));
    const cats: string[] = [];
    for (const g of classifyGroups) for (const it of g.items) if (it.category && !cats.includes(it.category)) cats.push(it.category);
    const qty = (mode: ClassifyMode, cat: string): number | null => {
      const it = groupByMode.get(mode)?.items.find((i) => i.category === cat);
      return it ? Number(it.quantity) : null;
    };
    const total = (mode: ClassifyMode): number => Number(groupByMode.get(mode)?.total || 0);
    return { presentModes: present, categories: cats, qtyOf: qty, totalOf: total };
  }, [classifyGroups]);

  const groupByMode = useMemo(() => new Map(classifyGroups.map((g) => [g.mode, g])), [classifyGroups]);

  // Cột "Nợ / trả" (chỉ đơn kiểm đếm, đặt NGAY TRƯỚC "Kiểm đếm giao"): bút toán sổ nợ đồ của đơn
  // (`item_debt_by_category`) gắn vào dòng loại đồ, hiện như phép cộng/trừ giữa đóng túi và số khách nhận.
  // Khớp theo `category_id` trước (sổ và log kiểm đếm cùng lấy từ danh mục), không có ID thì theo tên chuẩn hoá.
  // Bút toán không khớp dòng nào (loại đồ đổi tên, loại tự tạo…) → thành dòng riêng "(theo sổ)" để không mất số.
  const showDebtCol = Number(order?.requires_counting) === 1 && presentModes.includes("delivery_check");
  const debt = useMemo(() => {
    const byCat = new Map<string, Map<number, number>>();
    const extraRows: string[] = [];
    const total = new Map<number, number>();
    // Phần TRẢ NỢ cộng thêm vào ô "Kiểm đếm giao" theo dòng (đồ trả nợ nhập ở box "Trả nợ" riêng, KHÔNG nằm trong
    // số đếm của đơn). Đơn cũ (trước khi tách box) gõ luôn phần trả vào ô đếm → phần đếm vượt số nhận đã gồm trả nợ,
    // chỉ cộng phần còn lại để không tính hai lần.
    const returnExtra = new Map<string, number>();
    let returnExtraTotal = 0;
    let net = 0;
    const entries: any[] = Array.isArray(order?.item_debt_by_category) ? order.item_debt_by_category : [];
    if (!showDebtCol || entries.length === 0) {
      return { byCat, extraRows, total, net, returnExtra, returnExtraTotal, hasEntries: false };
    }

    const norm = (s: string) => String(s || "").trim().toLowerCase();
    const idByCat = new Map<string, number>();
    for (const g of classifyGroups) {
      for (const it of g.items) {
        if (it.category && it.category_id && !idByCat.has(it.category)) idByCat.set(it.category, Number(it.category_id));
      }
    }
    for (const e of entries) {
      const q = Number(e.quantity) || 0;
      const type = Number(e.entry_type);
      const eid = Number(e.category_id) || 0;
      let row = eid ? categories.find((c) => idByCat.get(c) === eid) : undefined;
      if (!row) row = categories.find((c) => norm(c) === norm(e.category));
      if (!row) {
        row = `${String(e.category || "").trim()} (theo sổ)`;
        if (!extraRows.includes(row)) extraRows.push(row);
      }
      const m = byCat.get(row) || new Map<number, number>();
      m.set(type, (m.get(type) || 0) + q);
      byCat.set(row, m);
      total.set(type, (total.get(type) || 0) + q);
      net += q;
    }
    for (const [row, m] of byCat) {
      const returned = Math.abs(Math.min(0, m.get(2) || 0));
      if (!returned) continue;
      const counted = qtyOf("delivery_check", row) || 0;
      const base = qtyOf("counting", row) || 0;
      const extra = Math.max(0, returned - Math.max(0, counted - base));
      if (extra > 0) {
        returnExtra.set(row, extra);
        returnExtraTotal += extra;
      }
    }
    return { byCat, extraRows, total, net, returnExtra, returnExtraTotal, hasEntries: true };
  }, [order, showDebtCol, classifyGroups, categories, qtyOf]);

  // Số KHÁCH THỰC NHẬN ở cột "Kiểm đếm giao" = số đếm của đơn + phần trả nợ (xem `returnExtra`).
  const deliveredOf = (cat: string): number | null => {
    const q = qtyOf("delivery_check", cat);
    const extra = debt.returnExtra.get(cat) || 0;
    if (q == null && !extra) return null;
    return (q || 0) + extra;
  };
  const shownQtyOf = (m: ClassifyMode, cat: string) => (m === "delivery_check" ? deliveredOf(cat) : qtyOf(m, cat));
  const shownTotalOf = (m: ClassifyMode) => totalOf(m) + (m === "delivery_check" ? debt.returnExtraTotal : 0);

  // Phiếu lỗi thất thoát gắn với từng CHẶNG (`shortage_data.stage_to`) → hiển thị mã phiếu + trạng thái ngay
  // dưới cột tương ứng. Phiếu mới nhất thắng nếu một chặng có nhiều phiếu (đổi người chịu trách nhiệm).
  const faultByStage = useMemo(() => {
    const map = new Map<string, any>();
    for (const f of faults || []) {
      const stage = f?.shortage_data?.stage_to;
      if (!stage) continue;
      const existing = map.get(stage);
      if (!existing || Number(f.id) > Number(existing.id)) map.set(stage, f);
    }
    return map;
  }, [faults]);

  // Thiết bị dùng trong SẢN XUẤT (sọt/máy giặt/máy sấy/bàn ủi/bàn gấp) — sắp xếp theo thời điểm gắn, cũ → mới.
  // Bỏ loại "Túi" (type=1): đã có breakdown riêng ở "Chi tiết theo túi" (khi nhận/đóng túi giao).
  const productionEquipment = useMemo(() => {
    return [...(equipmentDetail || [])]
      .filter((eq) => Number(eq.equipment_type) !== 1)
      .sort((a, b) => {
        const ta = parseDate(a.assigned_at)?.getTime() || 0;
        const tb = parseDate(b.assigned_at)?.getTime() || 0;
        return ta - tb;
      });
  }, [equipmentDetail]);

  // Số lượng đồ + thiết bị liên kết ghi nhận khi quét sọt/máy giặt lúc sản xuất (workflow_logs.classification_data,
  // mode='production_check') — khớp theo equipment_id với danh sách thiết bị ở trên. Mỗi thiết bị lấy log MỚI NHẤT.
  const productionCheckByEquipment = useMemo(() => {
    const map = new Map<number, { items: { category: string; quantity: number }[]; totalItems: number; linkedEquipmentCode?: string; ts: number }>();
    for (const l of workflowLogs || []) {
      const cd = parseCD(l.classification_data);
      if (!cd || cd.mode !== "production_check" || !cd.equipment_id) continue;
      const eid = Number(cd.equipment_id);
      const ts = new Date(l.performed_at || l.created_at || 0).getTime();
      const existing = map.get(eid);
      if (existing && existing.ts >= ts) continue;
      const items = Array.isArray(cd.items)
        ? cd.items.map((it: any) => ({ category: String(it.category || ""), quantity: Number(it.quantity) || 0 }))
        : [];
      map.set(eid, {
        items,
        totalItems: Number(cd.total_items) || items.reduce((s: number, it: any) => s + it.quantity, 0),
        linkedEquipmentCode: cd.linked_equipment_code ? String(cd.linked_equipment_code) : undefined,
        ts,
      });
    }
    return map;
  }, [workflowLogs]);

  // Hàng không chuẩn: danh sách mới nhất (log `defect_report` hoặc defects đính kèm log kiểm đếm) +
  // các túi (pickup_check) được đánh dấu `has_defect`.
  const nonstandard = useMemo(() => {
    let defects: any[] = [];
    let ts = -1;
    const bagMap = new Map<number, { code: string; hasDefect: boolean; ts: number }>();
    for (const l of workflowLogs || []) {
      const cd = parseCD(l.classification_data);
      if (!cd) continue;
      const t = new Date(l.performed_at || l.created_at || 0).getTime();
      if (Array.isArray(cd.defects) && t >= ts) {
        defects = cd.defects;
        ts = t;
      }
      if (cd.mode === "pickup_check" && Number(cd.equipment_id) > 0) {
        const eid = Number(cd.equipment_id);
        const ex = bagMap.get(eid);
        if (!ex || t > ex.ts) bagMap.set(eid, { code: cd.equipment_code || String(eid), hasDefect: cd.has_defect === true, ts: t });
      }
    }
    return {
      defects,
      bags: [...bagMap.values()].filter((b) => b.hasDefect).map((b) => b.code),
    };
  }, [workflowLogs]);

  // Lịch sử điều chỉnh SL / đơn giá (log `item_edit`) — thao tác của người có quyền
  // ORDER_ITEM_QUANTITY_EDIT, cần soi được khi tranh chấp.
  const itemEdits = useMemo(() => {
    const rows: { at: string; by: number; reason: string; lines: string[] }[] = [];
    for (const l of workflowLogs || []) {
      const cd = parseCD(l.classification_data);
      if (!cd || cd.type !== "item_edit") continue;
      const lines = [
        ...((cd.changes || []) as any[]).map((c) => `${c.item_name}: SL ${c.from} → ${c.to}`),
        ...((cd.price_changes || []) as any[]).map((c) => `${c.item_name}: đơn giá ${formatMoney(c.from)} → ${formatMoney(c.to)}`),
      ];
      rows.push({ at: l.performed_at || l.created_at, by: Number(l.employee_id) || 0, reason: cd.reason || "", lines });
    }
    return rows.sort((a, b) => (parseDate(a.at)?.getTime() || 0) - (parseDate(b.at)?.getTime() || 0));
  }, [workflowLogs]);

  // Tên CHI NHÁNH nơi thực hiện từng cột đối soát (thay nhãn chung):
  //  - inspect (phân loại chính) = chi nhánh CHỦ ĐƠN (cửa hàng gốc).
  //  - transfer_check (đối chiếu) = chi nhánh NHẬN điều chuyển (xưởng) — lấy từ phiếu đi (TO_BRANCH).
  const modeBranchName = useMemo(() => {
    const m: Partial<Record<ClassifyMode, string>> = {};
    if (order?.branch?.name) m.inspect = order.branch.name;
    const di = (transfers || [])
      .filter((t) => Number(t.status) !== 4)
      .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0))
      .find((t) => Number(t.direction) === 1);
    if (di?.to_branch_name) m.transfer_check = di.to_branch_name;
    return m;
  }, [order, transfers]);

  return (
    <div className="space-y-6">
      {/* ① Nhật ký tiến trình — thuần LOG: chuyện gì xảy ra, lúc nào, ai làm. Thời lượng ở bảng ②. */}
      <section>
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Nhật ký tiến trình</h4>
        {displayedSteps.length === 0 ? (
          <p className="text-sm italic text-slate-400">Chưa có log tiến trình.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left w-8">#</th>
                  <th className="px-3 py-2 text-center">Thời điểm</th>
                  <th className="px-3 py-2 text-left">Bước</th>
                  <th className="px-3 py-2 text-left">Người thực hiện</th>
                  <th className="px-3 py-2 text-left">Nơi thực hiện</th>
                  <th className="px-3 py-2 text-center">Hình ảnh</th>
                  <th className="px-3 py-2 text-left">Ghi chú / Bàn giao</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayedSteps.map((step, i) => (
                  <tr key={step.raw.id ?? i} className={`hover:bg-slate-50 ${step.isHandover ? "bg-indigo-50/40" : ""}`}>
                    <td className="px-3 py-2 text-slate-400 align-top">{i + 1}</td>
                    <td className="px-3 py-2 text-center align-top"><TimeCell v={step.toTime} /></td>
                    <td className="px-3 py-2 font-medium text-slate-800 align-top">
                      {step.label}
                      {step.isHandover && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-100 text-indigo-600">bàn giao</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 align-top">{step.performerName || nameOf(step.performerId)}</td>
                    <td className="px-3 py-2 align-top">
                      {step.plate ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-50 text-indigo-700">
                          Xe {step.plate}
                        </span>
                      ) : step.performedBranch ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-orange-50 text-orange-700">
                          {step.performedBranch}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-center">
                      {(() => {
                        const logImgs = getStepImages(step.raw);
                        // Ưu tiên ảnh trong log; nếu trống (đơn cũ) → lấy từ caption_images.
                        const imgs = logImgs.length ? logImgs : (captionImagesByStepIdx.get(i) || []);
                        if (imgs.length === 0) return <span className="text-slate-300">—</span>;
                        return (
                          <div className="flex flex-wrap gap-1 justify-center">
                            {imgs.map((p, k) => (
                              <div key={k} className="w-12 h-12 rounded overflow-hidden border border-slate-200">
                                <ImageCustom
                                  url={p}
                                  isLocal={false}
                                  isUser={false}
                                  isReview={true}
                                  className="w-12 h-12 object-cover cursor-pointer hover:opacity-90 transition"
                                />
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-slate-500 align-top">
                      {Number(step.raw.to_status) === 6 ? "" : (
                        <>
                          {prettyNote(step.raw.note || "")}
                          {(() => {
                            // Cảnh báo vị trí giao (`[CẢNH BÁO VỊ TRÍ]`) — BE đã ghi lat/lng thật vào
                            // classification_data lúc log, chỉ chưa hiện ra UI (note chỉ có khoảng cách).
                            const cd = parseCD(step.raw.classification_data);
                            if (cd?.action !== "DELIVERY_LOCATION_CHECK" || cd?.lat == null || cd?.lng == null) return null;
                            return (
                              <div className="mt-1">
                                <a
                                  href={`https://www.google.com/maps?q=${cd.lat},${cd.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 underline"
                                >
                                  {Number(cd.lat).toFixed(6)}, {Number(cd.lng).toFixed(6)}
                                </a>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ② Thời gian từng chặng — theo sơ đồ quy trình, gom NHẬN / SẢN XUẤT / GIAO */}
      {journeySegments.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Thời gian từng chặng</h4>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Chặng</th>
                  <th className="px-3 py-2 text-center w-24">Loại</th>
                  <th className="px-3 py-2 text-center">Từ</th>
                  <th className="px-3 py-2 text-center">Đến</th>
                  <th className="px-3 py-2 text-center">Thời gian</th>
                </tr>
              </thead>
              {journeyBlocks.map((block, bi) => {
                const sub = block.segs.reduce((s, x) => s + x.mins, 0);
                const subWait = block.segs.filter((x) => x.kind === "wait").reduce((s, x) => s + x.mins, 0);
                return (
                  <tbody key={bi} className="divide-y divide-slate-100 border-t border-slate-200">
                    <tr className="bg-slate-50">
                      <td className="px-3 py-1.5 text-[11px] font-bold tracking-wide text-slate-500" colSpan={4}>
                        {PHASE_LABEL[block.phase]}
                        {subWait > 0 && (
                          <span className="ml-2 font-normal text-amber-600">trong đó chờ {fmtMins(subWait)}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center text-[11px] font-bold text-slate-600">{fmtMins(sub)}</td>
                    </tr>
                    {block.segs.map((s, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-700 align-top">
                          <span className="font-medium">{s.label}</span>
                          {s.badges.length > 0 && (
                            <div className="mt-0.5 flex flex-wrap gap-1">
                              {s.badges.map((b, k) => (
                                <span key={k} className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-600">
                                  {b}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center align-top">
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${KIND_CLS[s.kind]}`}>
                            {KIND_LABEL[s.kind]}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center align-top"><TimeCell v={s.from} /></td>
                        <td className="px-3 py-2 text-center align-top"><TimeCell v={s.to} /></td>
                        <td className={`px-3 py-2 text-center font-semibold align-top ${s.kind === "wait" ? "text-amber-600" : "text-slate-700"}`}>
                          {fmtMins(s.mins)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                );
              })}
              <tfoot className="bg-slate-100 border-t border-slate-200">
                <tr className="font-semibold text-slate-800">
                  <td className="px-3 py-2" colSpan={4}>
                    Tổng thời gian xử lý đơn
                    <span className="ml-2 text-[11px] font-normal text-slate-500">
                      chờ {fmtMins(journeyTotals.wait)} · thực hiện {fmtMins(journeyTotals.work)} · vận chuyển {fmtMins(journeyTotals.transport)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center text-green-700">{fmtMins(journeyTotals.all)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {transferWeights.length > 0 && (
            <div className="mt-1.5 text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
              {transferWeights.map((w, i) => (
                <span key={i}>
                  Số kg nơi cân ({w.branch}): <b className="text-orange-700">{w.kg} kg</b>
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ② Kiểm đếm & Phân loại — ma trận đối soát */}
      <section>
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Kiểm đếm &amp; Phân loại (đối soát)</h4>
        {presentModes.length === 0 ? (
          <p className="text-sm italic text-slate-400">Chưa có dữ liệu kiểm đếm/phân loại.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Loại đồ</th>
                  {presentModes.map((m) => (
                    <React.Fragment key={m}>
                      {showDebtCol && m === "delivery_check" && (
                        <th className="px-3 py-2 text-center bg-sky-50" title="Bút toán sổ nợ đồ của đơn: − khách nhận thiếu, + khách nhận thêm (trả nợ)">
                          Nợ / trả
                        </th>
                      )}
                      <th className="px-3 py-2 text-center">{modeBranchName[m] || MODE_LABEL[m]}</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[...categories, ...debt.extraRows].map((cat) => {
                  // Dòng chỉ có trong sổ (loại đồ đổi tên / tự tạo) — không có số kiểm đếm, chỉ để tổng sổ đủ.
                  const ledgerOnly = debt.extraRows.includes(cat);
                  return (
                    <tr key={cat} className="hover:bg-slate-50">
                      <td className={`px-3 py-2 ${ledgerOnly ? "text-slate-500 italic" : "text-slate-700"}`}>{cat}</td>
                      {presentModes.map((m, idx) => {
                        const q = shownQtyOf(m, cat);
                        // So với MỐC GỐC (cột đầu — kiểm đếm khi nhận) chứ không phải cột liền trước.
                        // So cột liền trước cho ra chuỗi kiểu "-1 rồi +1" khi một bước nhập nhầm rồi bước sau
                        // đếm lại đúng → đọc như hai sự cố, trong khi thực tế đồ vẫn đủ.
                        const base = idx > 0 ? qtyOf(presentModes[0], cat) : null;
                        const changed = idx > 0 && base != null && q != null && q !== base;
                        const extra = m === "delivery_check" ? debt.returnExtra.get(cat) || 0 : 0;
                        return (
                          <React.Fragment key={m}>
                            {showDebtCol && m === "delivery_check" && (
                              <td className="px-3 py-2 text-center bg-sky-50/50">
                                <DebtChips byType={debt.byCat.get(cat)} />
                              </td>
                            )}
                            <td
                              className={`px-3 py-2 text-center font-medium ${changed ? "bg-red-50 text-red-600" : "text-slate-700"}`}
                              title={changed ? `Lệch ${q! - base!} so với ${MODE_LABEL[presentModes[0]]}` : undefined}
                            >
                              {q == null ? <span className="text-slate-300">—</span> : q}
                              {extra > 0 && (
                                <div className="text-[10px] font-normal text-slate-500 whitespace-nowrap">
                                  {q! - extra} + {extra} trả nợ
                                </div>
                              )}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {/* Tổng theo từng cột */}
                <tr className="bg-slate-50 font-semibold text-slate-800 border-t border-slate-200">
                  <td className="px-3 py-2">Tổng</td>
                  {presentModes.map((m) => (
                    <React.Fragment key={m}>
                      {showDebtCol && m === "delivery_check" && (
                        <td className="px-3 py-2 text-center bg-sky-50/50">
                          <DebtChips byType={debt.total} />
                        </td>
                      )}
                      <td className="px-3 py-2 text-center">
                        {shownTotalOf(m)}
                        {m === "delivery_check" && debt.returnExtraTotal > 0 && (
                          <div className="text-[10px] font-normal text-slate-500 whitespace-nowrap">
                            {totalOf(m)} + {debt.returnExtraTotal} trả nợ
                          </div>
                        )}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
                {/* Chênh lệch so với MỐC GỐC (cột đầu tiên). Đọc được thành câu: "hụt 1 từ bước phân loại,
                    tới lúc giao thì khớp lại" — thay vì chuỗi "-1 rồi +1" như khi so cột liền trước. */}
                <tr className="bg-slate-50 text-slate-600">
                  <td className="px-3 py-2">
                    Chênh lệch
                    <div className="text-[10px] font-normal text-slate-400">
                      so với {MODE_LABEL[presentModes[0]]}
                    </div>
                  </td>
                  {presentModes.map((m, idx) => {
                    let debtCell: React.ReactNode = null;
                    if (showDebtCol && m === "delivery_check") {
                      // Tự kiểm tra sổ: nhận − khách thực nhận (gồm trả nợ) phải bằng tổng bút toán của đơn
                      // (nợ/thất thoát/không chuẩn dương, trả/giao dư âm, điều chỉnh ±). Không có kiểm đếm nhận thì bỏ qua.
                      const hasBase = presentModes.includes("counting");
                      const gap = hasBase ? totalOf("counting") - shownTotalOf("delivery_check") - debt.net : 0;
                      debtCell = !hasBase ? (
                        <td className="px-3 py-2 text-center bg-sky-50/50" />
                      ) : (
                        <td
                          className={`px-3 py-2 text-center font-semibold bg-sky-50/50 ${gap === 0 ? "text-emerald-600" : "text-red-600"}`}
                          title="Kiểm đếm nhận − khách thực nhận, so với tổng bút toán sổ nợ đồ của đơn"
                        >
                          {gap === 0 ? "khớp sổ" : `lệch sổ ${gap > 0 ? `+${gap}` : gap}`}
                        </td>
                      );
                    }
                    if (idx === 0) {
                      return (
                        <React.Fragment key={m}>
                          {debtCell}
                          <td className="px-3 py-2 text-center text-slate-300">mốc gốc</td>
                        </React.Fragment>
                      );
                    }
                    const diff = shownTotalOf(m) - totalOf(presentModes[0]);
                    return (
                      <React.Fragment key={m}>
                        {debtCell}
                        <td className={`px-3 py-2 text-center font-semibold ${diff < 0 ? "text-red-600" : diff > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                          {diff === 0 ? "khớp" : diff > 0 ? `+${diff} (dư)` : `${diff} (thiếu)`}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
                {/* Lý do + TRẠNG THÁI PHIẾU của bước đó. Không gắn trạng thái thì lý do "Thất thoát" vẫn nằm
                    đó dù phiếu đã bị huỷ → người xem tưởng đơn vẫn đang có lỗi. */}
                {presentModes.some((m) => groupByMode.get(m)?.missingNote || faultByStage.get(m)) && (
                  <tr className="bg-slate-50 text-slate-600">
                    <td className="px-3 py-2 text-xs">Lý do / phiếu lỗi</td>
                    {presentModes.map((m) => {
                      const note = groupByMode.get(m)?.missingNote;
                      const fault = faultByStage.get(m);
                      const cancelled = fault && Number(fault.status) === FAULT_STATUS_REJECTED;
                      return (
                        <React.Fragment key={m}>
                          {showDebtCol && m === "delivery_check" && <td className="bg-sky-50/50" />}
                          <td className="px-3 py-2 text-center text-[11px] text-slate-600">
                            {note ? (
                              <span title={note} className={cancelled ? "line-through text-slate-400" : ""}>
                                {note}
                              </span>
                            ) : null}
                            {fault ? (
                              <div className={`mt-0.5 ${cancelled ? "text-slate-400" : "text-red-600"}`}>
                                {fault.code}
                                {cancelled ? " · đã huỷ" : ""}
                              </div>
                            ) : null}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                )}
                {/* KẾT LUẬN — điều người xem thực sự cần biết: cuối cùng khách nhận đủ hay thiếu. */}
                <tr className="border-t border-slate-200">
                  <td colSpan={presentModes.length + 1 + (showDebtCol ? 1 : 0)} className="px-3 py-2">
                    {(() => {
                      const last = presentModes[presentModes.length - 1];
                      const finalDiff = shownTotalOf(last) - totalOf(presentModes[0]);
                      const isFinalStep = last === "delivery_check";
                      if (!isFinalStep) {
                        return (
                          <span className="text-xs text-slate-500 italic">
                            Đơn chưa kiểm đếm với khách khi giao — số cuối cùng chưa chốt.
                          </span>
                        );
                      }
                      // Tổng khớp chưa chắc từng loại khớp (nợ loại này, trả nợ loại khác) → nêu rõ sổ nợ của đơn.
                      const owe = Math.max(0, debt.total.get(1) || 0);
                      const returned = Math.abs(Math.min(0, debt.total.get(2) || 0));
                      const debtNote = [
                        owe > 0 ? `ghi nợ ${owe} cái` : "",
                        returned > 0 ? `trả nợ ${returned} cái` : "",
                      ].filter(Boolean).join(", ");
                      return (
                        <span className={`text-xs font-semibold ${finalDiff === 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {finalDiff === 0
                            ? `✓ Khách nhận ${shownTotalOf(last)} cái, bằng tổng lúc nhận.`
                            : finalDiff < 0
                              ? `⚠ Khách nhận thiếu ${Math.abs(finalDiff)} cái so với lúc nhận.`
                              : `⚠ Khách nhận nhiều hơn lúc nhận ${finalDiff} cái.`}
                          {debtNote && <span className="font-normal text-slate-600"> Đơn này {debtNote}.</span>}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Hàng không chuẩn — ghi nhận khi kiểm đếm với khách (đơn kiểm đếm) hoặc tài xế (đơn thường) */}
        {(nonstandard.defects.length > 0 || nonstandard.bags.length > 0) && (() => {
          const DEFECT_LABEL: Record<string, string> = { tear: 'Rách', stain: 'Ố / vết bẩn khó sạch', fade: 'Bạc màu', other: 'Khác' };
          return (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-800 mb-2">
                Hàng không chuẩn{nonstandard.defects.length ? ` (${nonstandard.defects.length})` : ''}
              </p>
              <div className="space-y-2">
                {nonstandard.defects.map((d: any, i: number) => (
                  <div key={i} className="rounded-md bg-white border border-amber-100 p-2.5">
                    <div className="text-sm font-semibold text-slate-800">
                      {DEFECT_LABEL[d.type] || 'Khác'}
                      {d.category ? ` · ${d.category}${d.quantity ? ` (${d.quantity} cái)` : ''}` : ''}
                    </div>
                    {d.note && <div className="text-sm text-slate-600 mt-0.5">{d.note}</div>}
                    {Array.isArray(d.images) && d.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {d.images.map((img: string, k: number) => (
                          <ImageCustom
                            key={k}
                            url={img}
                            isLocal={false}
                            isUser={false}
                            isReview={true}
                            className="w-16 h-16 object-cover rounded border border-amber-200 cursor-pointer hover:opacity-90"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {nonstandard.bags.length > 0 && (
                <p className="text-xs font-semibold text-amber-700 mt-2">
                  Túi đựng hàng không chuẩn: {nonstandard.bags.join(', ')}
                </p>
              )}
            </div>
          );
        })()}

        {/* Điều chỉnh SL / đơn giá — thao tác của người có quyền ORDER_ITEM_QUANTITY_EDIT */}
        {itemEdits.length > 0 && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 mt-3">
            <h5 className="text-sm font-semibold text-indigo-800 mb-2">
              Điều chỉnh số lượng / đơn giá ({itemEdits.length})
            </h5>
            <div className="space-y-3">
              {itemEdits.map((e, i) => (
                <div key={i} className="text-xs text-slate-700">
                  <div className="text-slate-500">
                    {parseDate(e.at) ? fmtDateLines(parseDate(e.at)!).date + " " + fmtDateLines(parseDate(e.at)!).time : "—"}
                    {" · "}
                    {nameOf(e.by)}
                  </div>
                  <ul className="list-disc ml-4 mt-0.5">
                    {e.lines.map((ln, j) => (
                      <li key={j}>{ln}</li>
                    ))}
                  </ul>
                  {e.reason && <div className="text-slate-500 mt-0.5">Lý do: {e.reason}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chi tiết theo TỪNG TÚI (khi nhận) → Thiết bị sản xuất (giữa) → TỪNG TÚI (đóng túi giao) — 1 mạch nhận-sản xuất-giao */}
        {(() => {
          const DEFECT_LABEL: Record<string, string> = { tear: 'Rách', stain: 'Ố / vết bẩn khó sạch', fade: 'Bạc màu', other: 'Khác' };
          const renderBags = (pbMode: ClassifyMode) => {
            const grp: any = groupByMode.get(pbMode);
            const bags: any[] = grp?.bags || [];
            if (bags.length === 0) return null;
            return (
              <div key={pbMode} className="mt-3 space-y-2">
                <p className="text-xs font-semibold text-slate-600">
                  Chi tiết theo túi ({pbMode === 'pickup_check' ? 'khi nhận' : 'đóng túi giao'})
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {bags.map((bag, bi) => (
                    <div key={bi} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-semibold text-slate-700">{bag.code ? `Túi ${bag.code}` : 'Túi'}</span>
                        <span className="text-xs font-semibold text-cyan-600">{bag.total} cái</span>
                      </div>
                      {bag.items.length === 0 ? (
                        <p className="text-xs italic text-slate-400">Không nhập số lượng</p>
                      ) : (
                        <div className="space-y-0.5">
                          {bag.items.map((it: any, ii: number) => (
                            <div key={ii} className="flex justify-between text-xs text-slate-600">
                              <span>{it.category || '—'}</span>
                              <span className="font-semibold">x{it.quantity}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {Array.isArray(bag.defects) && bag.defects.length > 0 && (
                        <div className="mt-2 rounded bg-amber-50 border border-amber-200 p-2 space-y-1">
                          <p className="text-[11px] font-semibold text-amber-700">Lỗi đồ ghi nhận:</p>
                          {bag.defects.map((d: any, di: number) => (
                            <div key={di} className="text-[11px] text-slate-700">
                              <span className="font-medium text-amber-700">{DEFECT_LABEL[d.type] || d.type}</span>
                              {d.category ? ` · ${d.category}` : ''}
                              {d.note ? ` — ${d.note}` : ''}
                              {Array.isArray(d.images) && d.images.length > 0 ? (
                                <span className="text-slate-400"> ({d.images.length} ảnh)</span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          };
          return (
            <>
              {renderBags(PER_BAG_MODES[0])}
              {/* Sản xuất (giữa nhận-giao): sọt/máy giặt/máy sấy/bàn ủi/bàn gấp đã dùng, mỗi thẻ = 1 thiết bị. */}
              {productionEquipment.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-600">Chi tiết sản xuất (thiết bị sử dụng)</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {productionEquipment.map((eq, ei) => {
                      const pc = productionCheckByEquipment.get(Number(eq.equipment_id));
                      return (
                        <div key={eq.id ?? ei} className="rounded-lg border border-slate-200 p-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-semibold text-slate-700">
                              {EQUIPMENT_TYPE_NAME[Number(eq.equipment_type)] || `Loại ${eq.equipment_type}`} {eq.equipment_code}
                            </span>
                            {pc ? (
                              <span className="text-xs font-semibold text-cyan-600">{pc.totalItems} cái</span>
                            ) : (
                              <span className={`text-xs font-semibold ${Number(eq.status) === 1 ? "text-cyan-600" : "text-slate-400"}`}>
                                {Number(eq.status) === 1 ? "Đang dùng" : "Đã trả"}
                              </span>
                            )}
                          </div>
                          {pc && pc.items.length > 0 ? (
                            <div className="space-y-0.5 mb-1.5">
                              {pc.items.map((it, ii) => (
                                <div key={ii} className="flex justify-between text-xs text-slate-600">
                                  <span>{it.category || '—'}</span>
                                  <span className="font-semibold">x{it.quantity}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs italic text-slate-400 mb-1.5">Không ghi nhận số lượng</p>
                          )}
                          {pc?.linkedEquipmentCode && (
                            <p className="text-[11px] text-slate-500 mb-1.5">
                              Thiết bị liên quan: <span className="font-medium text-slate-700">{pc.linkedEquipmentCode}</span>
                            </p>
                          )}
                          <div className="space-y-0.5 text-xs text-slate-600 border-t border-slate-100 pt-1.5">
                            <div className="flex justify-between"><span>Người gắn</span><span className="font-medium text-slate-700">{nameOf(eq.assigned_by)}</span></div>
                            <div className="flex justify-between"><span>Gắn lúc</span><span className="font-medium text-slate-700">{fmtDateLines(eq.assigned_at) ? `${fmtDateLines(eq.assigned_at)!.date} ${fmtDateLines(eq.assigned_at)!.time}` : '—'}</span></div>
                            {eq.released_at && (
                              <div className="flex justify-between"><span>Trả lúc</span><span className="font-medium text-slate-700">{fmtDateLines(eq.released_at) ? `${fmtDateLines(eq.released_at)!.date} ${fmtDateLines(eq.released_at)!.time}` : '—'}</span></div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {renderBags(PER_BAG_MODES[1])}
            </>
          );
        })()}
        {/* <p className="text-[11px] text-slate-400 mt-1">Ô <span className="text-red-600 font-medium">đỏ</span> = SL khác cột liền trước. "Thiếu" = đếm sau ít hơn đếm trước.</p> */}
      </section>

      {/* ③ Lỗi / Phiếu phạt */}
      <section>
        <h4 className="text-sm font-semibold text-slate-700 mb-2">Lỗi / Phiếu phạt</h4>
        {faultsLoading ? (
          <p className="text-sm text-slate-400">Đang tải...</p>
        ) : !faults || faults.length === 0 ? (
          <p className="text-sm italic text-green-600">Không có lỗi/phiếu phạt cho đơn này.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Mã</th>
                  <th className="px-3 py-2 text-left">Loại lỗi</th>
                  <th className="px-3 py-2 text-left">Người chịu</th>
                  <th className="px-3 py-2 text-left">Mô tả</th>
                  <th className="px-3 py-2 text-right">Phạt</th>
                  <th className="px-3 py-2 text-center">Trạng thái</th>
                  <th className="px-3 py-2 text-center">Thời điểm</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {faults.map((f, i) => {
                  const st = FAULT_STATUS[Number(f.status)] || { text: "—", cls: "bg-slate-100 text-slate-600" };
                  return (
                    <tr key={f.id ?? i} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-700">{f.code || `#${f.id}`}</td>
                      <td className="px-3 py-2 text-slate-700">{FAULT_TYPE[Number(f.fault_type)] || "Khác"}</td>
                      <td className="px-3 py-2 text-slate-600">{f.responsible_name || (f.responsible_user_id ? `NV #${f.responsible_user_id}` : "—")}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {f.description || "—"}
                        {f.explanation ? <div className="text-[11px] text-slate-400 mt-0.5">Giải trình: {f.explanation}</div> : null}
                      </td>
                      <td className="px-3 py-2 text-right text-red-600 font-semibold">{Number(f.penalty_amount) > 0 ? `${formatMoney(f.penalty_amount)}đ` : "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${st.cls}`}>{st.text}</span>
                      </td>
                      <td className="px-3 py-2 text-center text-slate-500">{formatServerDateTime(f.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default OrderProcessTab;
